// Generación por kW de UNA cotización (ADENDA-DIEGO 12-oct, pedidos 2 y 3; etapa 3, MANDA):
//  - La base es la de Diego: 1,750 kWh por kWp al año (techo de 15 a 20°, conservadora), editable en Ajustes
//    (TablaPrecios.generacion_kwh_kwp, máximo 1,750).
//  - El vendedor la AJUSTA por obra con factores sugeridos (zona, inclinación y orientación, montaje, sombras) o la escribe;
//    queda en la bitácora quién la cambió y por qué. NUNCA sube de la base sin que el vendedor lo elija a sabiendas
//    (`sube_a_sabiendas`), y lo que sube sale solo de la ganancia bifacial sobre suelo o carport (comercio e industria).
//  - Todo lo prometido (dimensionar, ahorro, cobertura, retorno y el PDF) usa la generación de ESA cotización.
// Los datos (sol de los 72 municipios y los factores) viven en UN archivo, copiado de la investigación del 12-oct-2026 con su
// fuente y fecha: datos/radiacion-sonora.json. Aquí se validan al cargar (un dato mal copiado truena al arrancar).
// Isomorfo (lo usan el motor, el servidor y la interfaz): sin node:*.
import { z } from 'zod';
import DATOS from '../datos/radiacion-sonora.json' with { type: 'json' };
import { Texto } from './basicos.ts';
import type { TipoEstructura } from './cuestionario.ts';

// ───────────── Los datos (validados al cargar) ─────────────

/** Quita las claves "_…" (comentarios del archivo) en todos los niveles. */
function sinComentarios(x: unknown): unknown {
  if (Array.isArray(x)) return x.map(sinComentarios);
  if (x && typeof x === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(x)) if (!k.startsWith('_')) out[k] = sinComentarios(v);
    return out;
  }
  return x;
}

const Factor = z.number().positive().max(1.2);
const Clave = z.string().regex(/^26\d{3}$/, 'clave INEGI de un municipio de Sonora (26xxx)');

const MunicipioDatos = z.strictObject({
  clave: Clave,
  municipio: z.string().min(2).max(60),
  cabecera: z.string().min(2).max(60),
  pvgis_20: z.number().positive(),
  pvgis_0: z.number().positive(),
  pvgis_nsrdb_20: z.number().positive(),
  factor_era5: Factor,
  factor_nsrdb: Factor,
  factor_conservador: Factor,
  alerta: z.boolean(),
  sugerido_kwh_kwp: z.int().positive(),
});
export type MunicipioSonora = z.infer<typeof MunicipioDatos>;

const OpcionFactor = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  texto: z.string().min(3).max(90),
  factor: Factor,
  pvgis: z.string().optional(),
  nota: z.string().optional(),
  rango: z.string().optional(),
  calculo: z.string().optional(),
  /** Solo comercio e industria (suelo y carport: Diego, 12-oct tarde). */
  solo_negocio: z.boolean().optional(),
  /** La estructura del cuestionario con la que cuenta este montaje (verificación adversarial de la etapa 3: una obra de techo
   *  no puede prometer la ganancia bifacial de suelo o carport). Ausente = cualquier estructura (techo: no sube). */
  estructura: z.enum(['suelo', 'carport']).optional(),
  /** Estimación de la investigación (bifacial, albedo): aviso amarillo "por confirmar" hasta medir una obra real. */
  estimacion: z.boolean().optional(),
  /** Sombra fuerte: pedir visita técnica. */
  visita_tecnica: z.boolean().optional(),
});
export type OpcionFactorGeneracion = z.infer<typeof OpcionFactor>;

const Datos = z.strictObject({
  version: z.string(),
  consultado_el: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  fuente_documento: z.string(),
  fuente_corta: z.string().min(10).max(200),
  url: z.string().regex(/^https:\/\//),
  fuentes: z.record(z.string(), z.string()),
  base_kwh_kwp: z.literal(1750),
  base_regla: z.string(),
  hermosillo: z.strictObject({ pvgis_20: z.number().positive(), pvgis_0: z.number().positive(), pvgis_nsrdb_20: z.number().positive() }),
  umbral_alerta_factor: z.number().min(0.9).max(1),
  umbral_por_que: z.string(),
  redondeo_kwh_kwp: z.literal(10),
  municipios: z.array(MunicipioDatos).length(72),
  localidades: z.record(z.string(), Clave),
  fuera_de_sonora: z.strictObject({
    /** Clave INEGI de 2 dígitos → cómo escriben los vendedores ese estado (lectura de Solarix). */
    estados: z.record(z.string().regex(/^\d{2}$/), z.array(z.string().regex(/^[a-z ]{2,40}$/)).min(1)),
    ciudades: z.array(
      z.strictObject({
        ciudad: z.string(),
        /** Otros nombres de la misma ciudad (sin acentos), si los hay. */
        nombres: z.array(z.string().regex(/^[a-z ]{2,40}$/)).optional(),
        /** Clave INEGI de la cabecera (de la investigación): los 2 primeros dígitos son el estado. */
        clave_cabecera: z.string().regex(/^\d{9}$/),
        factor_conservador: Factor,
        pvgis_20: z.number().positive(),
      }),
    ),
  }),
  factores: z.strictObject({
    fuente: z.string(),
    inclinacion_y_orientacion: z.array(OpcionFactor).min(2),
    montaje: z.array(OpcionFactor).min(2),
    sombras: z.array(OpcionFactor).min(2),
    no_instalar: z.string(),
  }),
});

/** Redondeo de la investigación: a 10 kWh por kWp. */
export function redondearGeneracion(x: number): number {
  return Math.round(x / 10) * 10;
}

/** Lo que está mal en los datos de radiación (vacío = bien). Lo usa la carga del módulo (truena) y las pruebas. */
export function problemasDatosRadiacion(crudo: unknown): string[] {
  const r = Datos.safeParse(sinComentarios(crudo));
  if (!r.success) return r.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`);
  const d = r.data;
  const mal: string[] = [];
  const claves = new Set<string>();
  for (const m of d.municipios) {
    if (claves.has(m.clave)) mal.push(`${m.municipio}: clave ${m.clave} repetida`);
    claves.add(m.clave);
    if (Math.abs(m.factor_conservador - Math.min(m.factor_era5, m.factor_nsrdb)) > 1e-9) mal.push(`${m.municipio}: el factor conservador no es el menor de ERA5 y NSRDB`);
    if (m.alerta !== m.factor_conservador < d.umbral_alerta_factor) mal.push(`${m.municipio}: la alerta no sigue al umbral (${d.umbral_alerta_factor})`);
    const sugerido = m.alerta ? redondearGeneracion(d.base_kwh_kwp * m.factor_conservador) : d.base_kwh_kwp;
    if (m.sugerido_kwh_kwp !== sugerido) mal.push(`${m.municipio}: sugerido ${m.sugerido_kwh_kwp} y la regla da ${sugerido}`);
  }
  for (const [nombre, clave] of Object.entries(d.localidades)) if (!claves.has(clave)) mal.push(`localidad "${nombre}" apunta a ${clave}, que no está`);
  const f = d.factores;
  for (const [lista, base] of [
    [f.inclinacion_y_orientacion, 'sur_15_20'],
    [f.montaje, 'techo'],
    [f.sombras, 'sin_sombra'],
  ] as const) {
    if (new Set(lista.map((x) => x.id)).size !== lista.length) mal.push(`factores: ids repetidos en ${base}`);
    if (lista[0]?.id !== base || lista[0].factor !== 1) mal.push(`factores: la primera opción de cada lista es la base (${base}, factor 1)`);
  }
  // Lo único que puede subir de la base es la ganancia bifacial del montaje en suelo o carport (solo comercio e industria).
  for (const x of [...f.inclinacion_y_orientacion, ...f.sombras]) if (x.factor > 1) mal.push(`factores: ${x.id} sube la generación (${x.factor}); solo el montaje sobre suelo o carport puede`);
  for (const x of f.montaje) if (x.factor > 1 && !x.solo_negocio) mal.push(`factores: ${x.id} sube la generación y no dice "solo_negocio"`);
  // Verificación adversarial de la etapa 3: el montaje de suelo o carport va atado a esa estructura del cuestionario.
  for (const x of f.montaje) if (!!x.solo_negocio !== !!x.estructura) mal.push(`factores: ${x.id} es solo de negocio y no dice su "estructura" (suelo o carport), o al revés`);
  for (const c of d.fuera_de_sonora.ciudades) if (!d.fuera_de_sonora.estados[c.clave_cabecera.slice(0, 2)]) mal.push(`fuera de Sonora: ${c.ciudad} es del estado ${c.clave_cabecera.slice(0, 2)}, que no está en "estados"`);
  return mal;
}

function cargar(): z.infer<typeof Datos> {
  const mal = problemasDatosRadiacion(DATOS);
  if (mal.length) throw new Error(`datos/radiacion-sonora.json: ${mal.join('; ')}`);
  return Datos.parse(sinComentarios(DATOS));
}

export const DATOS_RADIACION = Object.freeze(cargar());

/** La base de Diego por omisión (ADENDA A0b): 1,750 kWh por kWp al año. Diego la cambia en Ajustes (máximo esta). */
export const GENERACION_BASE_POR_OMISION = DATOS_RADIACION.base_kwh_kwp;
/** Debajo de este factor (conservador) el municipio tiene "claramente menos sol" que Hermosillo (0.98, de la investigación). */
export const UMBRAL_MENOS_SOL = DATOS_RADIACION.umbral_alerta_factor;

export const OPCIONES_ORIENTACION: readonly OpcionFactorGeneracion[] = Object.freeze(DATOS_RADIACION.factores.inclinacion_y_orientacion);
export const OPCIONES_MONTAJE: readonly OpcionFactorGeneracion[] = Object.freeze(DATOS_RADIACION.factores.montaje);
export const OPCIONES_SOMBRAS: readonly OpcionFactorGeneracion[] = Object.freeze(DATOS_RADIACION.factores.sombras);
export const TEXTO_NO_INSTALAR_SOMBRA = DATOS_RADIACION.factores.no_instalar;

const mayor = (xs: readonly OpcionFactorGeneracion[]) => Math.max(...xs.map((x) => x.factor));
const menor = (xs: readonly OpcionFactorGeneracion[]) => Math.min(...xs.map((x) => x.factor));
/** Lo MÁS que una cotización puede prometer, a sabiendas: la base máxima (1,750) por el factor más alto del catálogo (grava
 *  blanca sobre suelo, 1.05) = 1,840. Sale de los datos, no se escribe a mano; la plantilla del PDF tiene el mismo tope. */
export const GENERACION_MAX_KWH_KWP = redondearGeneracion(GENERACION_BASE_POR_OMISION * mayor(OPCIONES_ORIENTACION) * mayor(OPCIONES_MONTAJE) * mayor(OPCIONES_SOMBRAS));
/** Lo menos que se acepta escribir: la base más baja que acepta Ajustes (1,200) por el peor caso del catálogo (zona, norte a
 *  20°, pegado a la lámina y sombra fuerte). Menos que eso es un error de dedo. */
export const GENERACION_MIN_KWH_KWP = redondearGeneracion(
  1_200 * Math.min(...DATOS_RADIACION.municipios.map((m) => m.factor_conservador)) * menor(OPCIONES_ORIENTACION) * menor(OPCIONES_MONTAJE) * menor(OPCIONES_SOMBRAS),
);

// ───────────── Municipio de la obra ─────────────

/** "Cd. Obregón, Son." → "cd obregon son": sin acentos, sin signos, minúsculas. */
export function normalizarLugar(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const POR_CLAVE = new Map(DATOS_RADIACION.municipios.map((m) => [m.clave, m] as const));
/** Nombre normalizado → clave: municipio, cabecera ("Heroica Caborca", "Ciudad Obregón") y localidades de Solarix. */
const NOMBRES: readonly (readonly [string, string])[] = Object.freeze(
  [
    ...DATOS_RADIACION.municipios.flatMap((m) => [
      [normalizarLugar(m.municipio), m.clave] as const,
      [normalizarLugar(m.cabecera), m.clave] as const,
      // "Villa Pesqueira (Mátape)" también como "Villa Pesqueira"
      [normalizarLugar(m.cabecera.replace(/\(.*\)/, '')), m.clave] as const,
    ]),
    ...Object.entries(DATOS_RADIACION.localidades).map(([n, c]) => [normalizarLugar(n), c] as const),
  ]
    .filter(([n]) => n.length >= 3)
    // el nombre más largo primero: "Magdalena de Kino" es Magdalena, no Kino
    .sort((a, b) => b[0].length - a[0].length),
);

export function municipioPorClave(clave: string | null | undefined): MunicipioSonora | null {
  return clave ? (POR_CLAVE.get(clave) ?? null) : null;
}

/** "Estado de Sonora, México" → "sonora": sin acentos ni signos, sin "estado de" al principio ni el país al final. */
export function normalizarEstado(estado: string): string {
  const n = normalizarLugar(estado).replace(/^(estado|edo) (de )?/, '');
  const sinPais = n.replace(/( (mexico|mex|mx))+$/, '').trim();
  // "Ciudad de México" y "Cd. México" se quedan como están (el "México" ahí es el nombre, no el país)
  return !sinPais || /(^| )(de|cd|ciudad)$/.test(sinPais) ? n : sinPais;
}

/** ¿El estado del cuestionario es Sonora? UNA sola regla para el motor, el servidor, el puente del PDF y la pantalla
 *  (verificación adversarial de la etapa 3: había tres y "Son." o "Sonora." las hacía contradecirse). Acepta "Sonora",
 *  "Son", "Son.", "Sonora.", "SONORA", "Sonora, México" y "Estado de Sonora". */
export function esSonora(estado: string | null | undefined): boolean {
  const n = normalizarEstado(estado ?? '');
  return n === 'sonora' || n === 'son';
}

/** Los municipios que nombra UN tramo de la ciudad (sin comas), por palabras completas. El nombre más largo se come a los que
 *  lleva dentro ("Magdalena de Kino" es Magdalena, no también Kino). */
function municipiosEnTramo(tramo: string): Set<string> {
  let t = ` ${tramo} `;
  const claves = new Set<string>();
  for (const [nombre, clave] of NOMBRES) {
    const k = ` ${nombre} `;
    if (!t.includes(k)) continue;
    claves.add(clave);
    t = t.split(k).join(' | ');
  }
  return claves;
}

/** El municipio de Sonora que nombra la ciudad del cuestionario ("Navojoa", "Cd. Obregón", "Bahía de Kino, Hermosillo").
 *  Con comas manda el ÚLTIMO tramo que nombre un municipio (ahí se escribe la ciudad: "Col. Los Álamos, Hmo" es Hermosillo,
 *  no Álamos). Si un tramo nombra dos municipios distintos ("Col. Rosario Hermosillo") no adivina. null = no lo reconoce o es
 *  ambiguo (el cotizador pregunta el municipio de la obra). Verificación adversarial de la etapa 3. */
export function municipioDeCiudad(ciudad: string): MunicipioSonora | null {
  const tramos = ciudad.split(/[,;/]/).map(normalizarLugar).filter(Boolean);
  for (let i = tramos.length - 1; i >= 0; i--) {
    const claves = municipiosEnTramo(tramos[i]!);
    if (claves.size > 1) return null;
    if (claves.size === 1) return POR_CLAVE.get([...claves][0]!) ?? null;
  }
  return null;
}

/** Los 72 municipios, por nombre (para elegir el de la obra cuando la ciudad no se reconoce). */
export const MUNICIPIOS_SONORA: readonly { clave: string; nombre: string }[] = Object.freeze(
  [...DATOS_RADIACION.municipios].map((m) => ({ clave: m.clave, nombre: m.municipio })).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
);

/** Fuera de Sonora: el factor de referencia de la investigación (solo dato; nada se aplica solo). Solo si coinciden la ciudad
 *  Y el estado (verificación adversarial de la etapa 3: "Juárez, Nuevo León" recibía el sol de Ciudad Juárez y "Cuauhtémoc,
 *  Chihuahua" el de la alcaldía Cuauhtémoc de la Ciudad de México). Sin estado reconocido, no hay referencia. */
export function referenciaFueraDeSonora(ciudad: string, estado: string): { ciudad: string; factor_conservador: number } | null {
  const fs = DATOS_RADIACION.fuera_de_sonora;
  const e = normalizarEstado(estado);
  const claveEstado = Object.entries(fs.estados).find(([, nombres]) => nombres.includes(e))?.[0];
  if (!claveEstado) return null;
  const t = ` ${normalizarLugar(ciudad)} `;
  // el nombre completo o su primera palabra si es distintiva ("Culiacán" por "Culiacán Rosales"; "Los" o "La" no cuentan)
  const llaves = (c: (typeof fs.ciudades)[number]) => {
    const n = normalizarLugar(c.ciudad);
    const primera = n.split(' ')[0]!;
    return [n, ...(primera.length >= 5 && primera !== n ? [primera] : []), ...(c.nombres ?? [])];
  };
  const r = fs.ciudades.find((c) => c.clave_cabecera.startsWith(claveEstado) && llaves(c).some((k) => t.includes(` ${k} `)));
  return r ? { ciudad: r.ciudad, factor_conservador: r.factor_conservador } : null;
}

/** El factor de la zona que se APLICA: solo baja (un municipio con más sol que Hermosillo nunca sube la base). */
export function factorZona(m: Pick<MunicipioSonora, 'factor_conservador'>): number {
  return Math.min(1, m.factor_conservador);
}

// ───────────── Lo que decide el vendedor (ParametrosCalculo.generacion) ─────────────

const existeEn = (xs: readonly OpcionFactorGeneracion[], que: string) => z.string().refine((id) => xs.some((x) => x.id === id), { message: `${que} que no está en el catálogo` });

export const FactoresGeneracion = z.strictObject({
  /** Aplicar el sol del municipio de la obra (solo baja). */
  zona: z.boolean(),
  orientacion: existeEn(OPCIONES_ORIENTACION, 'inclinación u orientación'),
  montaje: existeEn(OPCIONES_MONTAJE, 'montaje'),
  sombras: existeEn(OPCIONES_SOMBRAS, 'sombra'),
});
export type FactoresGeneracion = z.infer<typeof FactoresGeneracion>;

export const FACTORES_BASE: Readonly<FactoresGeneracion> = Object.freeze({ zona: false, orientacion: 'sur_15_20', montaje: 'techo', sombras: 'sin_sombra' });

/** Lo que el vendedor decide de la generación de ESTA cotización. Ausente = la base de Diego y el municipio de la ciudad.
 *  Verificación adversarial de la etapa 3 (12-oct): la cifra SIGUE a los factores. Si el vendedor no la escribe a mano
 *  (`kwh_kwp` null), el motor la calcula cada vez con la base de Diego de hoy, el municipio de la obra de hoy y los factores
 *  elegidos: si Diego baja la base o la obra cambia de ciudad, la cotización se mueve con ellos (antes se guardaba fija y se
 *  quedaba prometiendo la cifra vieja). */
export const GeneracionCotizacion = z
  .strictObject({
    /** Municipio de la obra (clave INEGI) cuando la ciudad del cuestionario no se reconoce. null = el de la ciudad. */
    municipio: Clave.refine((c) => POR_CLAVE.has(c), 'municipio que no está en la tabla de Sonora').nullable(),
    /** Los factores que eligió (lo que vio en la obra): con ellos se calcula la cifra, o se revisa la que escribió a mano. */
    factores: FactoresGeneracion,
    /** kWh por kWp al año ESCRITOS A MANO por el vendedor. null = los que dan los factores (con los factores de la base, la
     *  base de Diego). */
    kwh_kwp: z.number().min(GENERACION_MIN_KWH_KWP).max(GENERACION_MAX_KWH_KWP).nullable(),
    /** Por qué (queda en el historial y en la bitácora). Obligatorio si la cifra se escribe a mano. */
    motivo: Texto(3, 300).nullable(),
    /** El vendedor eligió prometer MÁS que la base sabiéndolo (bifacial sobre suelo o carport). Sin esto, nunca sube. */
    sube_a_sabiendas: z.boolean(),
    /** Lo que daban los factores cuando el vendedor escribió la cifra a mano: si después cambia (otra ciudad, otra base de
     *  Diego), el motor avisa que la cifra escrita ya no cuadra. Ausente en las cotizaciones de antes. */
    sugerida_kwh_kwp: z.number().min(GENERACION_MIN_KWH_KWP).max(GENERACION_MAX_KWH_KWP).nullable().optional(),
  })
  .superRefine((g, ctx) => {
    if (g.kwh_kwp !== null && g.motivo === null)
      ctx.addIssue({ code: 'custom', path: ['motivo'], message: 'Escribe por qué cambias la generación de esta obra (queda en la bitácora).' });
    if (g.kwh_kwp !== null && g.kwh_kwp !== Math.round(g.kwh_kwp))
      ctx.addIssue({ code: 'custom', path: ['kwh_kwp'], message: 'La generación va en kWh enteros por kWp al año.' });
  });
export type GeneracionCotizacion = z.infer<typeof GeneracionCotizacion>;

/** El campo del aviso "promete MÁS que la base, a sabiendas": el puente del PDF no lo copia a las notas de Diego porque la
 *  plantilla ya pone el suyo (ALTA) con la misma cifra (verificación adversarial de la etapa 3: salía dos o tres veces). */
export const CAMPO_GENERACION_A_SABIENDAS = '/parametros/generacion/sube_a_sabiendas';

export function opcionFactor(lista: readonly OpcionFactorGeneracion[], id: string): OpcionFactorGeneracion {
  const x = lista.find((o) => o.id === id);
  if (!x) throw new Error(`generación: "${id}" no está en el catálogo de factores`);
  return x;
}

export interface ParteSugerencia {
  que: 'zona' | 'orientacion' | 'montaje' | 'sombras';
  texto: string;
  factor: number;
}

/** ¿Este montaje cuenta en esta obra? Suelo y carport solo en comercio e industria (Diego, 12-oct tarde) y solo si la
 *  estructura del cuestionario es esa (verificación adversarial de la etapa 3: una obra de TECHO prometía la ganancia bifacial
 *  de suelo). null = cuenta; si no, por qué no. */
export function montajeNoCuenta(m: OpcionFactorGeneracion, negocio: boolean, estructura: TipoEstructura): string | null {
  if (m.solo_negocio && !negocio) return `«${m.texto}» (solo comercio e industria)`;
  if (m.estructura && m.estructura !== estructura)
    return `«${m.texto}» (el cuestionario dice que la estructura es ${estructura === 'ptr' ? 'PTR' : estructura === 'losa_lamina' ? 'losa o lámina' : estructura === 'suelo' ? 'sobre suelo' : 'carport'}: solo cuenta ${m.estructura === 'suelo' ? 'sobre suelo' : 'en carport'})`;
  return null;
}

/** La generación que SUGIEREN los factores: base × zona × (inclinación y orientación) × montaje × sombras, redondeada a 10
 *  (como la investigación). El montaje sobre suelo o carport solo cuenta en comercio e industria y con esa estructura en el
 *  cuestionario: en otra cosa se ignora y se dice. */
export function sugerenciaGeneracion(a: {
  base: number;
  factores: FactoresGeneracion;
  municipio: Pick<MunicipioSonora, 'municipio' | 'factor_conservador'> | null;
  negocio: boolean;
  estructura: TipoEstructura;
}): {
  kwh_kwp: number;
  partes: ParteSugerencia[];
  ignorados: string[];
  sube: boolean;
  estimacion: boolean;
  visita_tecnica: boolean;
} {
  const partes: ParteSugerencia[] = [];
  const ignorados: string[] = [];
  if (a.factores.zona) {
    if (!a.municipio) ignorados.push('el sol del municipio (falta elegir el municipio de la obra)');
    // un municipio con tanto sol como Hermosillo o más no cambia nada (la zona solo baja)
    else if (factorZona(a.municipio) < 1) partes.push({ que: 'zona', texto: `Sol de ${a.municipio.municipio}`, factor: factorZona(a.municipio) });
  }
  const o = opcionFactor(OPCIONES_ORIENTACION, a.factores.orientacion);
  const m = opcionFactor(OPCIONES_MONTAJE, a.factores.montaje);
  const s = opcionFactor(OPCIONES_SOMBRAS, a.factores.sombras);
  if (o.factor !== 1) partes.push({ que: 'orientacion', texto: o.texto, factor: o.factor });
  const noCuenta = montajeNoCuenta(m, a.negocio, a.estructura);
  if (noCuenta) ignorados.push(noCuenta);
  else if (m.factor !== 1) partes.push({ que: 'montaje', texto: m.texto, factor: m.factor });
  if (s.factor !== 1) partes.push({ que: 'sombras', texto: s.texto, factor: s.factor });
  const producto = partes.reduce((p, x) => p * x.factor, 1);
  const kwh = Math.min(GENERACION_MAX_KWH_KWP, Math.max(GENERACION_MIN_KWH_KWP, redondearGeneracion(a.base * producto)));
  return {
    kwh_kwp: kwh,
    partes,
    ignorados,
    sube: kwh > a.base,
    estimacion: !noCuenta && !!m.estimacion && m.factor !== 1,
    visita_tecnica: !!s.visita_tecnica,
  };
}

/** Lo MÁS que puede prometer esta cotización: la base, salvo que los factores elegidos den más (solo puede pasar con la
 *  ganancia bifacial de suelo o carport en comercio e industria, con esa estructura en el cuestionario). Verificación
 *  adversarial de la etapa 3: el tope es lo que dan TODOS los factores elegidos (sol, inclinación, montaje y sombras), no solo
 *  el montaje: un carport a 5° con sombra fuerte no justifica subir nada. */
export function topeGeneracion(a: Parameters<typeof sugerenciaGeneracion>[0]): number {
  return Math.max(a.base, sugerenciaGeneracion(a).kwh_kwp);
}

/** "Sol de Navojoa × 0.957 · Al sur, 10° × 0.96": los factores aplicados, para la bitácora y la pantalla. */
export function textoFactores(partes: readonly ParteSugerencia[]): string {
  return partes.length ? partes.map((p) => `${p.texto} × ${p.factor.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}`).join(' · ') : 'sin factores (la base)';
}

/** Lo que el motor usó (ResultadoMotor.generacion): lo ven todos (no hay costos). */
export const GeneracionCalculada = z.strictObject({
  /** La base de Diego (Ajustes o 1,750). */
  base_kwh_kwp: z.number().positive(),
  /** La de ESTA cotización: lo que se promete (dimensionar, ahorro, cobertura, retorno y PDF). */
  kwh_kwp: z.number().positive(),
  /** El vendedor la cambió (≠ base). */
  de_vendedor: z.boolean(),
  /** Promete más que la base (a sabiendas). */
  sube_sobre_base: z.boolean(),
  motivo: Texto(3, 300).nullable(),
  /** El vendedor ESCRIBIÓ la cifra (si no, la sacan los factores con la base y el municipio de hoy). Ausente en los cálculos
   *  de antes de la verificación adversarial de la etapa 3. */
  a_mano: z.boolean().optional(),
  /** Lo que dan hoy los factores elegidos (con la base de Diego y el sol del municipio de la obra). */
  factores_kwh_kwp: z.number().positive().optional(),
  /** ADENDA z46-8 (P20): el sol del municipio de la obra se aplicó SOLO (parámetro `sol_del_municipio` y municipio con alerta),
   *  sin que el vendedor lo eligiera. Ausente = no (o un cálculo de antes). */
  zona_sola: z.boolean().optional(),
  /** El municipio de la obra (de la ciudad o elegido) y lo que dice su sol. null = no se reconoce (o fuera de Sonora). */
  municipio: z
    .strictObject({
      clave: Clave,
      nombre: Texto(2, 60),
      como: z.enum(['ciudad', 'elegido']),
      factor_conservador: Factor,
      alerta: z.boolean(),
      sugerido_kwh_kwp: z.number().positive(),
    })
    .nullable(),
});
export type GeneracionCalculada = z.infer<typeof GeneracionCalculada>;
